import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Button } from '@/components/ui/button';
import { ArrowRight, Stethoscope, Building2, DollarSign, HeartHandshake, Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [surgeries, setSurgeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSurgeries();
  }, []);

  const fetchSurgeries = async () => {
    try {
      const response = await axios.get(`${API}/surgeries`);
      setSurgeries(response.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching surgeries:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Stethoscope,
      title: t.experiencedDoctors,
      description: t.experiencedDoctorsDesc,
    },
    {
      icon: Building2,
      title: t.modernFacilities,
      description: t.modernFacilitiesDesc,
    },
    {
      icon: DollarSign,
      title: t.affordablePrices,
      description: t.affordablePricesDesc,
    },
    {
      icon: HeartHandshake,
      title: t.completeSupport,
      description: t.completeSupportDesc,
    },
  ];

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-50 via-white to-blue-50 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              {t.heroTitle}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {t.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button
                  size="lg"
                  className="btn-primary bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-6 rounded-full text-lg shadow-lg"
                  data-testid="get-quote-btn"
                >
                  {t.getQuote}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/surgeries">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 rounded-full text-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                  data-testid="learn-more-btn"
                >
                  {t.learnMore}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Medical Video Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Découvrez Notre Excellence Médicale' : 
               language === 'en' ? 'Discover Our Medical Excellence' : 
               'اكتشف تميزنا الطبي'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'fr' ? 'Des installations de classe mondiale et une expertise médicale reconnue internationalement' : 
               language === 'en' ? 'World-class facilities and internationally recognized medical expertise' : 
               'مرافق عالمية المستوى وخبرة طبية معترف بها دوليًا'}
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            {/* Video Container */}
            <div className="relative aspect-video bg-gradient-to-br from-red-100 via-white to-blue-100">
              {/* Video from Pexels - Free medical video */}
              <video
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                data-testid="medical-video"
              >
                <source src="https://videos.pexels.com/video-files/5473807/5473807-uhd_2732_1440_25fps.mp4" type="video/mp4" />
                {language === 'fr' ? 'Votre navigateur ne supporte pas la vidéo.' : 
                 language === 'en' ? 'Your browser does not support the video tag.' : 
                 'متصفحك لا يدعم علامة الفيديو.'}
              </video>
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/20 to-transparent pointer-events-none"></div>
              
              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8">
                <div className="text-center max-w-3xl">
                  <h3 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
                    {language === 'fr' ? 'VIP Medicale Services' : 
                     language === 'en' ? 'VIP Medicale Services' : 
                     'VIP Medicale Services'}
                  </h3>
                  <p className="text-xl md:text-2xl mb-6 drop-shadow-md">
                    {language === 'fr' ? 'Votre Partenaire Santé de Confiance' : 
                     language === 'en' ? 'Your Trusted Health Partner' : 
                     'شريك صحتك الموثوق'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Service Badges - Moved below video */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8 mb-4">
            <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <Stethoscope className="h-5 w-5 text-red-600" />
              <span className="text-sm font-semibold text-gray-900">
                {language === 'fr' ? 'Soins de Qualité' : 
                 language === 'en' ? 'Quality Care' : 
                 'رعاية جودة'}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <Building2 className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-semibold text-gray-900">
                {language === 'fr' ? 'Hôpitaux Modernes' : 
                 language === 'en' ? 'Modern Hospitals' : 
                 'مستشفيات حديثة'}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <HeartHandshake className="h-5 w-5 text-red-600" />
              <span className="text-sm font-semibold text-gray-900">
                {language === 'fr' ? 'Accompagnement 24/7' : 
                 language === 'en' ? '24/7 Support' : 
                 'دعم 24/7'}
              </span>
            </div>
          </div>
          
          {/* Features Grid Below Video */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-2">15+</div>
              <p className="text-sm text-gray-600 font-medium">
                {language === 'fr' ? 'Ans d\'Expérience' : 
                 language === 'en' ? 'Years Experience' : 
                 'سنوات خبرة'}
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent mb-2">50+</div>
              <p className="text-sm text-gray-600 font-medium">
                {language === 'fr' ? 'Chirurgiens Experts' : 
                 language === 'en' ? 'Expert Surgeons' : 
                 'جراح خبير'}
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-2">5000+</div>
              <p className="text-sm text-gray-600 font-medium">
                {language === 'fr' ? 'Patients Satisfaits' : 
                 language === 'en' ? 'Satisfied Patients' : 
                 'مرضى راضون'}
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent mb-2">100%</div>
              <p className="text-sm text-gray-600 font-medium">
                {language === 'fr' ? 'Certifié JCI' : 
                 language === 'en' ? 'JCI Certified' : 
                 'معتمد JCI'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
            {t.whyChooseUs}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="card-hover p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
                  data-testid={`feature-${index}`}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            {t.ourServices}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            {loading ? (
              <div className="flex justify-center">
                <div className="spinner"></div>
              </div>
            ) : surgeries.length > 0 ? (
              <span>{t.viewAllSurgeries}</span>
            ) : null}
          </p>

          {!loading && surgeries.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {surgeries.map((surgery) => (
                  <Link
                    key={surgery.id}
                    to={`/surgeries/${surgery.id}`}
                    className="card-hover block"
                    data-testid={`surgery-card-${surgery.id}`}
                  >
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full">
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {surgery[`name_${language}`]}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-2">
                          {surgery[`description_${language}`]}
                        </p>
                        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                          <div>
                            <p className="text-sm text-gray-500">{t.from}</p>
                            <p className="text-2xl font-bold text-red-600">
                              €{surgery.price_eur}
                            </p>
                          </div>
                          <Button variant="ghost" className="text-red-600">
                            <ArrowRight className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center">
                <Link to="/surgeries">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-full"
                    data-testid="view-all-surgeries-btn"
                  >
                    {t.viewAllSurgeries}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t.readyToStart}
          </h2>
          <p className="text-xl text-blue-100 mb-8">{t.readyToStartDesc}</p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 rounded-full text-lg"
              data-testid="cta-contact-btn"
            >
              {t.contactUs}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Home;